import { useMutation, useQuery } from "react-query";
import _ from "lodash";
import {
  getAllUser,
  bulkAssignGrantLetterTemplate,
  bulkUploadGrantLetterTemplates,
  deleteEmailTemplateAsset,
  deleteGrantLetterTemplate,
  getCCEmailSettings,
  getESignSettings,
  getEmailSettings,
  getEmailTemplateAssets,
  getEmailTemplateContent,
  getEmailTemplateNames,
  getEmployeePortalSettings,
  getGrantLetterSettings,
  getGrantLetterTemplates,
  listEmailTemplates,
  saveEmailTemplateContent,
  setCCEmailSettings,
  setDeleteDefaultSignature,
  setESginSettingDetails,
  setEmailSettingDetails,
  setEmployeePortalSettingDetails,
  setGrantLetterSettingDetails,
  uploadEmailTemplateAsset,
  uploadGrantLetterTemplateFiles,
} from "../api/siteSettings";
import { queryClient } from "./client";

export function useGetEmployeePortalSettings() {
  return useQuery({
    queryKey: "getEmployeePortalSettings",
    queryFn: getEmployeePortalSettings,
  });
}
export function useGetEmailSettings() {
  return useQuery({
    queryKey: "getEmailSettings",
    queryFn: getEmailSettings,
    select: (data) => {
      const newData = _.omit(data, ["sendCCEmailsToAdmins"]);
      delete data.sendCCEmailsToAdmins;
      delete data.emailTemplateCC;
      return data;
    },
  });
}
export function useGetGrantLetterTemplates() {
  return useQuery({
    queryKey: "getGrantLetterTemplates",
    queryFn: getGrantLetterTemplates,
  });
}
export function useGetGrantLetterSettings() {
  return useQuery({
    queryKey: "getGrantLetterSettings",
    queryFn: getGrantLetterSettings,
  });
}
export function useESginSettings() {
  return useQuery({
    queryKey: "getESignSettings",
    queryFn: getESignSettings,
  });
}
export function useSetEmployeePortalSettings() {
  return useMutation({
    mutationKey: "setEmployeePortalSettingDetails",
    mutationFn: setEmployeePortalSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmployeePortalSettings");
      queryClient.refetchQueries("getEmployeePortalSettings");
    },
  });
}

export function useSetEmailSettings() {
  return useMutation({
    mutationKey: "setEmailSettingDetails",
    mutationFn: setEmailSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmailSettings");
      queryClient.refetchQueries("getEmailSettings");
    },
  });
}
export function useSetGrantLetterSettings() {
  return useMutation({
    mutationKey: "setGrantLetterSettingDetails",
    mutationFn: setGrantLetterSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterSettings");
      queryClient.refetchQueries("getGrantLetterSettings");
    },
  });
}
export function useDeleteDefaultSignature() {
  return useMutation({
    mutationFn: setDeleteDefaultSignature,
    mutationKey: "setDeleteDefaultSignature",
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterSettings");
      queryClient.refetchQueries("getGrantLetterSettings");
    },
  });
}

export function useSetESignSettings() {
  return useMutation({
    mutationKey: "setESginSettingDetails",
    mutationFn: setESginSettingDetails,
    onSuccess: () => {
      queryClient.invalidateQueries("getESignSettings");
      queryClient.refetchQueries("getESignSettings");
    },
  });
}
export function useGetEmailTemplateNames() {
  const excludeTemplates = [
    "EsopAmendment",
    "EsopRejected",
    "EsopActive",
    "EsopAmendmentReject",
    "EsopApproval",
  ];
  return useQuery({
    queryKey: "emailTemplateNames",
    queryFn: listEmailTemplates,
    select: (data) =>
      data.filter(
        (template) => !excludeTemplates.includes(template.templateName)
      ),
  });
}
export function useGetEmailTemplateContent(templateId: string) {
  return useQuery({
    queryKey: ["emailTemplate", templateId],
    queryFn: getEmailTemplateContent,
    enabled: false,
  });
}

export function useSaveEmailTemplate() {
  return useMutation({
    mutationKey: "saveEmailTemplate",
    mutationFn: saveEmailTemplateContent,
  });
}

export function useGetAllCompanyUsers() {
  return useQuery({
    queryKey: "allCompanyUsers",
    queryFn: getAllUser,
  });
}

export function useSaveEmailCCSettings() {
  return useMutation({
    mutationKey: "saveEmailCCSettings",
    mutationFn: setCCEmailSettings,
    onSuccess: () => {
      queryClient.invalidateQueries("getEmailCCSettings");
      queryClient.refetchQueries("getEmailCCSettings");
    },
  });
}

export function useGetEmailCCSettings() {
  return useQuery({
    queryKey: "getEmailCCSettings",
    queryFn: getCCEmailSettings,
  });
}

export function useBulkAssignGrantLetterTemplate() {
  return useMutation({
    mutationKey: "bulkAssignGrantLetterTemplate",
    mutationFn: bulkAssignGrantLetterTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
    },
  });
}

export function useBulkUploadGrantLetterTemplates() {
  return useMutation({
    mutationKey: "bulkUploadGrantLetterTemplates",
    mutationFn: bulkUploadGrantLetterTemplates,
  });
}

export function useDeleteGrantLetterTemplate() {
  return useMutation({
    mutationKey: "deleteGrantLetterTemplate",
    mutationFn: deleteGrantLetterTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
    },
  });
}

export function useUploadGrantLetterTemplateFiles() {
  return useMutation({
    mutationKey: "uploadGrantLetterTemplateFiles",
    mutationFn: uploadGrantLetterTemplateFiles,
    onSuccess: () => {
      queryClient.invalidateQueries("getGrantLetterTemplates");
      queryClient.refetchQueries("getGrantLetterTemplates");
    },
  });
}

export const EMAIL_TEMPLATE_ASSETS_KEY = "getEmailTemplateAssets";

export function useGetEmailTemplateAssets() {
  return useQuery({
    queryKey: EMAIL_TEMPLATE_ASSETS_KEY,
    queryFn: getEmailTemplateAssets,
  });
}

export function useUploadEmailTemplateAsset() {
  return useMutation({
    mutationKey: "uploadEmailTemplateAsset",
    mutationFn: uploadEmailTemplateAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(EMAIL_TEMPLATE_ASSETS_KEY);
    },
  });
}

export function useDeleteEmailTemplateAsset() {
  return useMutation({
    mutationKey: "deleteEmailTemplateAsset",
    mutationFn: deleteEmailTemplateAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(EMAIL_TEMPLATE_ASSETS_KEY);
    },
  });
}
